import React from 'react';
import { useCurrentFrame } from 'remotion';
import type { CutWindow, SceneLayoutBlock } from '@/lib/transitions/buildSceneLayout';
import { resolveEditorPlaybackFrame } from '@/lib/preview/resolveEditorPlaybackFrame';
import { EditorPlaybackVideo } from '@/components/preview/EditorPlaybackVideo';

const shellStyle = (style: React.CSSProperties | null): React.CSSProperties => ({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  ...(style ?? {}),
});

/** Browser preview for a cut-up clip: one video element, plus a second only
 *  during an overlap blend. */
export const EditorPlaybackStack: React.FC<{
  videoUrl: string;
  blocks: SceneLayoutBlock[];
  cuts: CutWindow[];
  fps: number;
  baseVolume: number;
  muted?: boolean;
  playing?: boolean;
  objectFit?: 'contain' | 'cover' | 'fill';
  onBufferingChange?: (buffering: boolean) => void;
  onReady?: () => void;
  onMediaError?: (error: Error) => void;
  renderPrimary?: (video: React.ReactNode) => React.ReactNode;
  renderIncoming?: (video: React.ReactNode) => React.ReactNode;
}> = ({
  videoUrl,
  blocks,
  cuts,
  fps,
  baseVolume,
  muted = false,
  playing = false,
  objectFit = 'contain',
  onBufferingChange,
  onReady,
  onMediaError,
  renderPrimary = (video) => video,
  renderIncoming = (video) => video,
}) => {
  const frame = useCurrentFrame();
  const playback = resolveEditorPlaybackFrame({ frame, blocks, cuts, fps, baseVolume });
  const shared = { videoUrl, playing, muted, objectFit, onMediaError };

  return (
    <>
      <div style={shellStyle(playback.primary.style)}>
        {renderPrimary(
          <EditorPlaybackVideo
            {...shared}
            sourceTime={playback.primary.sourceTime}
            volume={muted ? 0 : playback.primary.volume}
            reportBuffering
            onBufferingChange={onBufferingChange}
            onReady={onReady}
          />,
        )}
      </div>
      {playback.incoming && (
        <div style={shellStyle(playback.incoming.style)}>
          {renderIncoming(
            <EditorPlaybackVideo
              {...shared}
              sourceTime={playback.incoming.sourceTime}
              volume={muted ? 0 : playback.incoming.volume}
              reportBuffering={false}
            />,
          )}
        </div>
      )}
    </>
  );
};
